import { Link } from 'react-router';
import { AlertTriangle } from 'lucide-react';
import { useMigrationReports } from '@/hooks/useNotifications.ts';
import { usePermission } from '@/hooks/usePermission.ts';
import { useEntitlement, MODULES } from '@/hooks/useEntitlement.ts';
import { Button } from '@/components/ui/button.tsx';

/**
 * "Your paging was rewritten — here is what happened to it."
 *
 * §4 strips channel actions off alert definitions and re-expresses them as routes.
 * §11 calls that the one moment in this design where somebody could stop getting paged
 * and never find out, and mitigates it with a report **plus a banner** — because a
 * report nobody is told about is a log, and §11 is explicit that a banner cannot read
 * a log.
 *
 * It lives in the app-wide banner slot rather than on the routing page for exactly
 * that reason: the person who needs to know is the one who would not think to look.
 *
 * **It is not dismissible.** Every other banner in the product is, with a 30-day TTL,
 * and that is right for announcements. This one is a claim that somebody checked their
 * paging survived, which is what per-row acknowledgement records — dismissing it would
 * silence the question without answering it. It disappears when the last row is
 * acknowledged, and not before.
 */
export function MigrationReportBanner() {
  // Gated, not merely hidden: the endpoint requires `notification_route:read`, so
  // fetching without it is a 403 on every page load for everyone else.
  //
  // Asked at *organization* scope, because that is what the endpoint authorizes
  // against. Asking by name alone made this true for every division administrator
  // — whose grant covers their division and not the organization — so the banner
  // fetched, the server refused with `wrong_context`, and the global interceptor
  // raised a "switch context?" dialog on whatever page they were looking at.
  const canRead = usePermission('notification_route:read', 'organization');
  // Gated on the entitlement too, and not merely for symmetry: this banner is
  // deliberately not dismissible — it clears only when every row is acknowledged — and
  // the acknowledge control lives in a section that renders nothing without the
  // entitlement. Gating on permission alone gives an unentitled organization a banner
  // it can never clear, pointing at a page that shows it nothing.
  const entitled = useEntitlement(MODULES.notificationRoutes);
  const visible = canRead && entitled;
  const { data } = useMigrationReports(visible);

  if (!visible || !data || data.unacknowledged === 0) return null;

  const n = data.unacknowledged;

  return (
    <div className="w-full border-b border-sev-3/30 bg-sev-3/10 px-4 py-2.5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="flex items-center gap-2 text-sm min-w-0">
          <AlertTriangle className="h-4 w-4 text-sev-3 shrink-0" />
          <span>
            {n === 1
              ? 'One alert action was converted into a notification route during an upgrade.'
              : `${String(n)} alert actions were converted into notification routes during an upgrade.`}{' '}
            <span className="text-muted-foreground">Check that the right people still get paged.</span>
          </span>
        </span>
        <Button asChild size="sm" variant="outline" className="shrink-0">
          {/* The routing page is where the reports are listed and acknowledged. */}
          <Link to="/admin/notifications/routing">Review</Link>
        </Button>
      </div>
    </div>
  );
}
